// import { DownloadOutlined } from '@ant-design/icons';
import type { ProColumns } from '@ant-design/pro-components';
import { Button } from 'antd';
import React from 'react';
import useAppMessage from '@/hooks/useAppMessage';
import {
  type ExportColumn,
  type ExportSummary,
  exportTableToCSV,
} from '@/utils/exportTable';

/**
 * 获取嵌套对象的值
 */
function getNestedValue(obj: any, path?: string | string[]): unknown {
  if (!path) return '';
  if (typeof path === 'string') {
    return obj[path] ?? '';
  }
  // 处理嵌套路径，如 ['user', 'name']
  let value: unknown = obj;
  for (const key of path) {
    if (value && typeof value === 'object' && key in value) {
      value = (value as Record<string, unknown>)[key];
    } else {
      return '';
    }
  }
  return value ?? '';
}

export interface ExportButtonProps<T = any> {
  columns: ProColumns<T>[];
  dataSource: T[];
  fileName?: string;
  summary?: ExportSummary;
  disabled?: boolean;
  buttonText?: string;
  onExport?: () => void; // 导出前的回调
}

function getValueEnumText<T>(
  col: ProColumns<T>,
  value: unknown,
): string | undefined {
  const valueEnum = col.valueEnum as
    | Record<string, { text?: string }>
    | undefined;
  if (!valueEnum) return undefined;
  const key = String(value);
  const item = valueEnum[key];
  if (!item) return undefined;
  return item.text ? String(item.text) : undefined;
}

function extractTextFromNode(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node
      .map((item) => extractTextFromNode(item))
      .filter((item) => item !== '')
      .join('\n')
      .trim();
  }
  if (React.isValidElement(node)) {
    const props = (node.props || {}) as { children?: React.ReactNode };
    return extractTextFromNode(props.children).trim();
  }
  return '';
}

function normalizeExportText(text: string): string {
  return text
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

function getColumnTitleText<T>(col: ProColumns<T>): string {
  const title = extractTextFromNode(col.title as React.ReactNode);
  return normalizeExportText(title || '');
}

/**
 * 导出按钮组件
 * 用于 ProTable 的 toolBarRender 中
 */
const ExportButton = <T = any>({
  columns,
  dataSource,
  fileName,
  summary,
  disabled,
  buttonText = '导出',
  onExport,
}: ExportButtonProps<T>): React.ReactElement => {
  const appMessage = useAppMessage();
  const handleExport = () => {
    if (dataSource.length === 0) {
      appMessage.warning('当前没有可导出的数据');
      return;
    }

    try {
      // 转换 ProColumns 为 ExportColumn
      const exportColumns: ExportColumn<T>[] = columns
        .filter(
          (col) =>
            !(col as any).hideInExport &&
            !(col as any).hideInTable &&
            col.valueType !== 'option' &&
            col.title,
        ) // 过滤导出隐藏列、表格隐藏列（筛选项）、操作列和没有标题的列
        .map((col) => {
          // 获取数据索引
          const dataIndex = col.dataIndex as string | string[] | undefined;

          // 对于 dateTime 类型的列，直接使用原始值，不使用 render 函数
          const isDateTime = col.valueType === 'dateTime';
          // 对于 index 类型的列（序号列），需要特殊处理
          const isIndex = col.valueType === 'index';

          return {
            title: getColumnTitleText(col),
            dataIndex,
            render: isIndex
              ? (_: unknown, __: T, index: number) => {
                  // 序号列：直接返回索引 + 1
                  return String(index + 1);
                }
              : isDateTime
                ? (value: unknown, record: T) => {
                    // 对于时间字段，直接使用原始值，并在前后添加空格以确保 Excel 列宽足够
                    if (dataIndex) {
                      const recordValue = getNestedValue(
                        record as any,
                        dataIndex,
                      );
                      if (recordValue) {
                        // 在时间字符串前后添加空格，帮助 Excel 自动调整列宽
                        // 同时使用制表符来增加列宽
                        return `\t${String(recordValue)}\t`;
                      }
                      return '';
                    }
                    return value ? `\t${String(value)}\t` : '';
                  }
                : col.render
                  ? (value: unknown, record: T, index: number) => {
                      try {
                        const valueEnumText = getValueEnumText(col, value);
                        // 调用 ProColumns 的 render 函数
                        const rendered = (col.render as any)?.(
                          value,
                          record,
                          index,
                          undefined,
                          {
                            ...col,
                            value,
                          },
                        );

                        // 如果返回的是字符串或数字，直接返回
                        if (
                          typeof rendered === 'string' ||
                          typeof rendered === 'number'
                        ) {
                          return String(rendered);
                        }

                        // 如果返回 null 或 undefined，返回空字符串
                        if (rendered === null || rendered === undefined) {
                          return '';
                        }

                        // 如果是 React 元素，尝试提取文本内容
                        if (rendered && typeof rendered === 'object') {
                          const renderedText = normalizeExportText(
                            extractTextFromNode(rendered as React.ReactNode),
                          );
                          if (renderedText) {
                            return renderedText;
                          }

                          // 兼容自定义组件通过 label 传文字（例如 Tag 组件）
                          if (
                            'props' in rendered &&
                            rendered.props &&
                            'label' in
                              (rendered.props as Record<string, unknown>) &&
                            (typeof (rendered.props as Record<string, unknown>)
                              .label === 'string' ||
                              typeof (rendered.props as Record<string, unknown>)
                                .label === 'number')
                          ) {
                            return String(
                              (rendered.props as Record<string, unknown>).label,
                            );
                          }
                        }

                        // 优先使用 valueEnum 的文本映射（如 true/false -> 启用/停用）
                        if (valueEnumText) {
                          return valueEnumText;
                        }

                        // 如果无法提取文本，使用原始值或从 record 中获取
                        if (value !== null && value !== undefined) {
                          return String(value);
                        }

                        // 如果 value 为空，尝试从 record 中获取
                        if (dataIndex) {
                          const recordValue = getNestedValue(
                            record as any,
                            dataIndex,
                          );
                          return recordValue ? String(recordValue) : '';
                        }

                        return '';
                      } catch (error) {
                        console.warn('导出时 render 函数执行失败:', error);
                        // 降级处理：使用原始值
                        return value ? String(value) : '';
                      }
                    }
                  : undefined,
            hideInExport: (col as any).hideInExport,
          };
        });

      // 导出前的回调
      onExport?.();

      // 执行导出
      exportTableToCSV({
        columns: exportColumns,
        dataSource,
        fileName,
        summary,
      });

      appMessage.success('数据已导出');
    } catch (error) {
      console.error('导出失败:', error);
      appMessage.error('导出失败，请重新尝试');
    }
  };

  return (
    <Button
      // icon={<DownloadOutlined />}
      onClick={handleExport}
      type="primary"
      ghost
      disabled={disabled || dataSource.length === 0}
    >
      {buttonText}
    </Button>
  );
};

export default ExportButton;
